import React, { useEffect, useRef, useState } from 'react'
import { Button, Drawer, Input, Modal, theme } from 'antd'
import { CheckOutlined, CloseOutlined, SearchOutlined } from '@ant-design/icons'
import { FilterConfig, FilterOption, OptionSwatch, isSearchable, matchesQuery, panelOptions } from '../filter-trigger'

export interface FilterRowLabels {
  filters: string
  more: string
  clearAll: string
  apply: string
  /** Rendered as `${showResults} {count}` context via replace - see showResultsLabel(). */
  showResults: string
  discardTitle: string
  discardBody: string
  discardConfirm: string
  keepEditing: string
  /** Shown inside a filter's dropdown (and inside a drawer section) when it has zero options. */
  noOptions: string
  /** Placeholder and accessible name of the search box a long list gets. */
  search: string
  /** Shown when the search matches nothing; `{query}` is replaced with what was typed. */
  noResults: string
  /** Accessible name of a group's arrow while the group is closed; `{group}` is replaced with its name. */
  expandGroup: string
  /** Accessible name of a group's arrow while the group is open; `{group}` is replaced with its name. */
  collapseGroup: string
}

export const DEFAULT_LABELS: FilterRowLabels = {
  filters: 'Filters',
  more: 'More',
  clearAll: 'Clear all',
  apply: 'Apply',
  showResults: 'Show {count} results',
  discardTitle: 'Discard filter changes?',
  discardBody: 'Your unapplied filter changes will be lost.',
  discardConfirm: 'Discard',
  keepEditing: 'Keep editing',
  noOptions: 'No data',
  search: 'Search',
  noResults: 'No results for “{query}”',
  expandGroup: 'Show {group}',
  collapseGroup: 'Hide {group}',
}

/** Minimum tap target on touch surfaces. */
const TAP_TARGET = 44

export interface FilterDrawerProps {
  open: boolean
  configs: FilterConfig[]
  applied: Record<string, string[]>
  onApply: (values: Record<string, string[]>) => void
  onClose: () => void
  resultCount?: number
  /** Debounced 300ms while the user stages changes, so the caller can compute resultCount. */
  onStagedChange?: (staged: Record<string, string[]>) => void
  labels: FilterRowLabels
}

const sameValues = (a: Record<string, string[]>, b: Record<string, string[]>, configs: FilterConfig[]) =>
  configs.every((config) => {
    const left = [...(a[config.id] ?? [])].sort().join(',')
    const right = [...(b[config.id] ?? [])].sort().join(',')
    return left === right
  })

/**
 * The mobile filter surface: a bottom sheet where selections are STAGED, not applied - nothing
 * changes behind the sheet until "Show N results" is tapped. Every section renders its options
 * as toggle chips (role="checkbox", ≥44px targets) - one style for all sections, whatever the
 * option count. The × discards, confirming first when staged state differs from applied. The
 * footer shows a live count when the caller supplies one, and a static Apply label when it
 * can't - never a stale number.
 */
export function FilterDrawer(props: FilterDrawerProps) {
  const { open, configs, applied, onApply, onClose, resultCount, onStagedChange, labels } = props
  const { token } = theme.useToken()
  const [staged, setStaged] = useState<Record<string, string[]>>(applied)
  const [confirmingDiscard, setConfirmingDiscard] = useState(false)
  const [queries, setQueries] = useState<Record<string, string>>({})
  const stagedChangeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Staged state re-seeds from `applied` only when the sheet opens - while it is open the
  // user's staging must not be overwritten by background changes to the applied values.
  useEffect(() => {
    if (open) {
      setStaged(applied)
      setQueries({})
    }
  }, [open])

  useEffect(() => {
    if (!open || !onStagedChange) return
    if (stagedChangeTimer.current) clearTimeout(stagedChangeTimer.current)
    stagedChangeTimer.current = setTimeout(() => onStagedChange(staged), 300)
    return () => {
      if (stagedChangeTimer.current) clearTimeout(stagedChangeTimer.current)
    }
  }, [staged, open, onStagedChange])

  const dirty = !sameValues(staged, applied, configs)

  const toggleStaged = (config: FilterConfig, optionValue: string) => {
    setStaged((previous) => {
      const current = previous[config.id] ?? []
      if (config.multiple === false) {
        return { ...previous, [config.id]: current[0] === optionValue ? [] : [optionValue] }
      }
      return {
        ...previous,
        [config.id]: current.includes(optionValue) ? current.filter((entry) => entry !== optionValue) : [...current, optionValue],
      }
    })
  }

  const requestClose = () => {
    if (dirty) setConfirmingDiscard(true)
    else onClose()
  }

  const chip = (config: FilterConfig, option: FilterOption) => {
    const selected = (staged[config.id] ?? []).includes(option.value)
    return (
      <button
        key={option.value}
        type="button"
        role="checkbox"
        aria-checked={selected}
        className="vt-filter-drawer-chip"
        onClick={() => toggleStaged(config, option.value)}
        style={{
          minHeight: TAP_TARGET,
          padding: `0 ${token.padding}px`,
          borderRadius: token.borderRadius,
          border: `1px solid ${selected ? token.colorPrimaryBorder : token.colorBorder}`,
          background: selected ? token.colorPrimaryBg : token.colorBgContainer,
          color: selected ? token.colorPrimary : token.colorText,
          fontSize: token.fontSize,
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          gap: token.paddingXXS,
        }}
      >
        {option.swatch && <OptionSwatch color={option.swatch} />}
        {option.label}
        {selected && <CheckOutlined aria-hidden style={{ fontSize: 11 }} />}
      </button>
    )
  }

  const sectionBody = (config: FilterConfig) => {
    const query = queries[config.id] ?? ''
    const ordered = panelOptions(config)
    const options = query.trim() ? ordered.filter((option) => option.group || matchesQuery(option.label, query)) : ordered
    // A group heading becomes a title over its options' chips; the drawer has no room to open and close groups.
    const blocks: { title?: FilterOption; options: FilterOption[] }[] = []
    for (const option of options) {
      if (option.group) blocks.push({ title: option, options: [] })
      else if (blocks.length === 0 || (!option.indentLevel && blocks[blocks.length - 1].title)) blocks.push({ options: [option] })
      else blocks[blocks.length - 1].options.push(option)
    }
    const shown = blocks.filter((block) => block.options.length > 0)
    return (
      <>
        {isSearchable(config) && (
          <Input
            allowClear
            prefix={<SearchOutlined aria-hidden style={{ color: token.colorTextTertiary }} />}
            placeholder={labels.search}
            aria-label={labels.search}
            value={query}
            onChange={(event) => setQueries((previous) => ({ ...previous, [config.id]: event.target.value }))}
            style={{ marginBottom: token.paddingXS }}
          />
        )}
        {shown.length === 0 && (
          <div role="status" style={{ color: token.colorTextSecondary, fontSize: token.fontSize, padding: `${token.paddingXS}px 0` }}>
            {config.options.length === 0 ? labels.noOptions : labels.noResults.replace('{query}', query.trim())}
          </div>
        )}
        {shown.map(({ title, options: blockOptions }) => (
          <div key={title?.value ?? blockOptions[0].value} role="group" aria-label={title?.label} style={{ display: 'grid', gap: token.paddingXS, marginBottom: token.paddingXS }}>
            {title && <span style={{ color: token.colorTextSecondary, fontSize: token.fontSize }}>{title.label}</span>}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: token.paddingXS }}>{blockOptions.map((option) => chip(config, option))}</div>
          </div>
        ))}
      </>
    )
  }

  const footerLabel = resultCount === undefined ? labels.apply : labels.showResults.replace('{count}', String(resultCount))

  return (
    <>
      <style>{`
        .vt-filter-drawer-chip:focus-visible {
          outline: 2px solid ${token.colorPrimary};
          outline-offset: 2px;
        }
        /* The app's global drawer.less forces .ant-drawer-body { padding: 0 !important },
           which beats the inline styles.body padding - win it back with a scoped rule. */
        .vt-filter-drawer .ant-drawer-body {
          padding: ${token.padding}px !important;
        }
      `}</style>
      <Drawer
        open={open}
        className="vt-filter-drawer"
        placement="bottom"
        height="85%"
        closable={false}
        onClose={requestClose}
        styles={{
          body: { padding: token.padding },
          content: { borderRadius: `${token.borderRadiusLG}px ${token.borderRadiusLG}px 0 0`, overflow: 'hidden' },
        }}
        title={
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Button type="text" shape="circle" icon={<CloseOutlined />} aria-label={labels.discardTitle} onClick={requestClose} />
            <span style={{ fontWeight: 500 }}>{labels.filters}</span>
            <Button type="link" onClick={() => setStaged(Object.fromEntries(configs.map((config) => [config.id, []])))}>
              {labels.clearAll}
            </Button>
          </div>
        }
        footer={
          <Button type="primary" block style={{ minHeight: TAP_TARGET }} onClick={() => onApply(staged)}>
            {footerLabel}
          </Button>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: token.paddingLG }}>
          {configs.map((config) => (
            <section key={config.id} aria-labelledby={`vt-filter-drawer-${config.id}`}>
              <div id={`vt-filter-drawer-${config.id}`} style={{ fontSize: 12, fontWeight: 500, color: token.colorTextSecondary, marginBottom: token.paddingXS }}>
                {config.label}
              </div>
              {sectionBody(config)}
            </section>
          ))}
        </div>
      </Drawer>

      <Modal
        open={confirmingDiscard}
        width={400}
        title={labels.discardTitle}
        okText={labels.discardConfirm}
        okButtonProps={{ danger: true }}
        cancelText={labels.keepEditing}
        onOk={() => {
          setConfirmingDiscard(false)
          onClose()
        }}
        onCancel={() => setConfirmingDiscard(false)}
      >
        {labels.discardBody}
      </Modal>
    </>
  )
}
